/**
 * Turn a git ref chip's text into the refs it names, each with the path it can
 * be viewed at on the repository's GitHub host.
 *
 * The chip text comes from {@link ./shell-summary.ts}, which flattens a git
 * command's operands into one display string — `origin refs/heads/feat/rows`,
 * `to origin main`, `main..feat/rows`. This module walks that text back into
 * structure so the chip can offer real links, under the same honesty rule the
 * summary follows: a token is only linked when its shape says what it is.
 * Placeholder phrases ("a branch", "3 paths"), revision expressions
 * (`HEAD~2`, `@{u}`), and refspecs (`main:main`) are left unlinked rather
 * than guessed at.
 *
 * Pure string work, no React and no network: the repo the paths hang off is
 * the caller's problem, so this stays unit-testable as data.
 */

export type GitRefKind = 'branch' | 'commit' | 'tag' | 'pull';

export interface GitRefLink {
  /** The ref exactly as the chip shows it (`refs/heads/main`, `origin/main`). */
  ref: string;
  /** The name a reader knows it by: the branch or tag, the sha, `#123`. */
  label: string;
  kind: GitRefKind;
  /** Path under the repository web URL (`tree/main`, `commit/abc1234`). */
  refPath: string;
}

export interface GitRefSubject {
  refs: GitRefLink[];
  /**
   * The two sides of a `a..b` range, when both resolved to distinct
   * comparable refs — the familiar `main → feat/x` reading of a diff.
   */
  range: { from: GitRefLink; to: GitRefLink } | null;
  /** GitHub compare path for `range` (`compare/a...b`). Set together with it. */
  comparePath: string | null;
}

/**
 * Words the summary uses in placeholder phrases ("a branch", "the upstream
 * branch", "3 paths") and joiners ("a and b"). None of them is a ref.
 */
const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'the',
  'branch',
  'branches',
  'commit',
  'commits',
  'tag',
  'tags',
  'paths',
  'upstream',
  'working',
  'tree',
]);

/**
 * Remote names worth recognizing without context. `origin/main` is almost
 * always the remote-tracking branch; a local branch literally named
 * `origin/main` is pathological enough to lose the tie-break.
 */
const KNOWN_REMOTES = new Set(['origin', 'upstream']);

/** `HEAD`, `FETCH_HEAD`, `ORIG_HEAD`, … — positions, not refs a host can show. */
const HEAD_LIKE = /^[A-Z_]*HEAD$/;

/**
 * Shas are hex, but so are some words (`defaced`); requiring a digit rules
 * those out while keeping essentially every real abbreviated sha.
 */
const COMMIT_SHA = /^(?=.*\d)[0-9a-f]{7,40}$/i;

/** Anything a plain branch/tag/sha never contains: revision syntax, refspecs, globs. */
const UNLINKABLE = /[~^:*\\[\]{}@\s]/;

/** Percent-encode a ref for a URL path, keeping its `/` separators. */
function encodeRefPath(ref: string): string {
  return ref.split('/').map(encodeURIComponent).join('/');
}

function classify(candidate: string, remote: string | null): GitRefLink | null {
  if (
    candidate.length === 0 ||
    candidate.endsWith('…') ||
    candidate.startsWith('-') ||
    UNLINKABLE.test(candidate) ||
    HEAD_LIKE.test(candidate) ||
    STOP_WORDS.has(candidate) ||
    // A bare remote (`git fetch origin`) names a source, not a ref.
    candidate === remote ||
    KNOWN_REMOTES.has(candidate) ||
    /^\d+$/.test(candidate)
  ) {
    return null;
  }

  const pull = /^(?:refs\/)?pull\/(\d+)(?:\/\w+)?$/.exec(candidate);
  if (pull) {
    return { ref: candidate, label: `#${pull[1]}`, kind: 'pull', refPath: `pull/${pull[1]}` };
  }

  const tag = /^refs\/tags\/(.+)$/.exec(candidate);
  if (tag) {
    // `tree/<tag>` resolves whether or not the tag has a release page.
    return { ref: candidate, label: tag[1], kind: 'tag', refPath: `tree/${encodeRefPath(tag[1])}` };
  }

  const head = /^refs\/heads\/(.+)$/.exec(candidate);
  if (head) {
    return {
      ref: candidate,
      label: head[1],
      kind: 'branch',
      refPath: `tree/${encodeRefPath(head[1])}`,
    };
  }

  const tracking = /^refs\/remotes\/[^/]+\/(.+)$/.exec(candidate);
  if (tracking) {
    return {
      ref: candidate,
      label: tracking[1],
      kind: 'branch',
      refPath: `tree/${encodeRefPath(tracking[1])}`,
    };
  }

  // Any other fully-qualified ref (`refs/stash`, notes) has no page to link.
  if (candidate.startsWith('refs/')) return null;

  if (COMMIT_SHA.test(candidate)) {
    return { ref: candidate, label: candidate, kind: 'commit', refPath: `commit/${candidate}` };
  }

  // `origin/main` — a remote-tracking name; the host only knows the branch.
  const [first, ...rest] = candidate.split('/');
  if (rest.length > 0 && (first === remote || KNOWN_REMOTES.has(first))) {
    const name = rest.join('/');
    return { ref: candidate, label: name, kind: 'branch', refPath: `tree/${encodeRefPath(name)}` };
  }

  return {
    ref: candidate,
    label: candidate,
    kind: 'branch',
    refPath: `tree/${encodeRefPath(candidate)}`,
  };
}

/**
 * Parse a `ref`-kind chip's text into linkable refs. Returns empty `refs` for
 * text that names nothing linkable ("origin", "3 paths", "the upstream
 * branch"), which callers should treat as "keep the plain chip".
 */
export function parseGitRefSubject(text: string): GitRefSubject {
  // Push subjects read "to origin main"; the preposition is display, not a ref.
  const toPush = text.startsWith('to ');
  let tokens = (toPush ? text.slice(3) : text).split(/\s+/).filter(Boolean);

  // A leading remote name. Every producer of a space-joined multi-token
  // subject is `git push`/`fetch`/`pull`, whose first operand is always the
  // repository; list-ish subjects ("a and b") join with "and" and never lead
  // with a remote. A push target is the repository even on its own. The one
  // shape left ambiguous is a lone unknown word (`git fetch fork` reads like
  // `git checkout fork`), which stays a branch — the honest majority reading.
  let remote: string | null = null;
  const [lead] = tokens;
  if (
    lead !== undefined &&
    !lead.includes('/') &&
    !lead.includes('.') &&
    (toPush || (tokens.length > 1 && !tokens.includes('and')))
  ) {
    remote = lead;
    tokens = tokens.slice(1);
  }

  const refs: GitRefLink[] = [];
  const seen = new Set<string>();
  let rangePair: GitRefSubject['range'] = null;
  let comparePath: string | null = null;

  for (const token of tokens) {
    // `a..b` and `a...b` name two refs and, together, a comparison. Either
    // side may be empty (`main..` means "…to HEAD"): the named side still
    // links, and a token holding a range never classifies whole.
    const range = /^(.*?)\.{2,3}(.*)$/.exec(token);
    const candidates = (range ? [range[1], range[2]] : [token]).filter((c) => c.length > 0);
    const links = candidates.map((candidate) => classify(candidate, remote));
    if (
      range &&
      links.length === 2 &&
      links[0] &&
      links[1] &&
      // A pull ref's label (`#123`) is display, not a committish the compare
      // view can resolve; a same-name comparison is empty by construction.
      links[0].kind !== 'pull' &&
      links[1].kind !== 'pull' &&
      links[0].label !== links[1].label &&
      !comparePath
    ) {
      rangePair = { from: links[0], to: links[1] };
      comparePath = `compare/${encodeRefPath(links[0].label)}...${encodeRefPath(links[1].label)}`;
    }
    for (const link of links) {
      if (!link || seen.has(link.refPath)) continue;
      seen.add(link.refPath);
      refs.push(link);
    }
  }

  return { refs, range: rangePair, comparePath };
}
